'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Cancel01Icon } from '@terra/ui-icons';
import { TraceExpandChevron } from './TraceExpandChevron';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Collapsible } from '../../../../components/collapsible';
import { MetaDefault } from '../../../../components/typography';
import { MarkdownPreview } from '../../../markdown-preview';
import {
  shouldShowToolHarSection,
  type TracePhase,
  type TraceToolCall,
} from '../types';
import { renderToolOutput } from '../utils/tool-output-renderer';
import { isMcpToolCallViewerTool } from './McpToolCallOutputBody';
import { ToolArgsRenderer } from './ToolArgsRenderer';
import { PythonToolHarSection } from './PythonToolHarSection';
import { useToolCallHarAvailability } from './useToolCallHarAvailability';
import {
  getTraceToolRenderConfig,
  isHttpExchangeTool,
  normalizeToolName,
} from '../utils/tool-config';
import { shouldShowTraceToolShimmer } from '../utils/trace-text-shimmer';
import { AiBrowserStreamingFrame } from '../../components/AiBrowserStreamingFrame';
import type { SseSubscribe } from '@terra/shared-sse-contracts';
import { isTraceBrowserStreamingTool } from '../utils/trace-browser-streaming.util';

type StandardToolCardProps = {
  toolCall: TraceToolCall;
  displayName: string;
  isEndTestTool?: boolean;
  tracePhase?: TracePhase;
  preferHarOverToolOutput?: boolean;
  showWithoutHar?: boolean;
  onHideMessage?: () => void;
  onSSESubscribe?: SseSubscribe;
};

function endToolMessage(
  args: Record<string, unknown> | undefined,
): string | undefined {
  const candidates = [args?.summary, args?.conclusion, args?.finalMessage];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate;
    }
  }

  return undefined;
}

function shouldDefaultOpenToolCard({
  toolCall,
  tracePhase,
  isEndTestTool,
}: {
  toolCall: TraceToolCall;
  tracePhase: TracePhase;
  isEndTestTool: boolean;
}): boolean {
  if (isEndTestTool) {
    return true;
  }

  if (tracePhase === 'co-pilot' && isHttpExchangeTool(toolCall.name)) {
    return true;
  }

  const normalizedName = normalizeToolName(toolCall.name);
  const isRunning =
    toolCall.status === 'started' || toolCall.status === 'in-progress';

  if (
    tracePhase === 'ai-discovery' &&
    isTraceBrowserStreamingTool(normalizedName) &&
    isRunning
  ) {
    return true;
  }

  return false;
}

export function StandardToolCard({
  toolCall,
  displayName,
  isEndTestTool = false,
  tracePhase = 'attack',
  preferHarOverToolOutput = false,
  showWithoutHar = false,
  onHideMessage,
  onSSESubscribe,
}: StandardToolCardProps) {
  const t = useTranslations('designSystem.chat');
  const endToolBody = isEndTestTool ? endToolMessage(toolCall.args) : undefined;
  const [isOpen, setIsOpen] = useState(() =>
    shouldDefaultOpenToolCard({ toolCall, tracePhase, isEndTestTool }),
  );
  const normalizedName = normalizeToolName(toolCall.name);
  const usesMcpToolCallViewer = isMcpToolCallViewerTool(toolCall.name);
  const renderConfig = getTraceToolRenderConfig(toolCall.name, {
    preferHarOverToolOutput:
      preferHarOverToolOutput && !isEndTestTool && !usesMcpToolCallViewer,
  });
  const isHarGatedHttpTool =
    renderConfig.preferHarOverToolOutput && isHttpExchangeTool(toolCall.name);
  const harAvailability = useToolCallHarAvailability({
    toolCallId: toolCall.id,
    refreshKey: toolCall.status,
    enabled: isHarGatedHttpTool,
  });
  const hasError = Boolean(toolCall.error);
  const hasOutput = Boolean(toolCall.output);
  const hasResult = hasOutput || hasError;
  const hasArgs = toolCall.args && Object.keys(toolCall.args).length > 0;
  const shouldShowErrorBlock =
    normalizedName !== 'navigate-web' &&
    !renderConfig.hideRawError &&
    hasError &&
    !hasOutput &&
    !usesMcpToolCallViewer;
  const shouldShowArgs =
    normalizedName !== 'navigate-web' &&
    !renderConfig.hideArgs &&
    !usesMcpToolCallViewer;
  const toolOutputForRender =
    toolCall.output ||
    (renderConfig.hideRawError || !shouldShowErrorBlock
      ? toolCall.error
      : undefined);
  const isFinishedToolCall =
    toolCall.status === 'completed' || toolCall.status === 'error';
  const outputShowsHttpExchange =
    hasOutput && isHttpExchangeTool(toolCall.name);
  const isAwaitingHarContent =
    isHarGatedHttpTool &&
    !isFinishedToolCall &&
    renderConfig.preferHarOverToolOutput;
  const hasHarContent =
    isHarGatedHttpTool &&
    (isAwaitingHarContent ||
      harAvailability.isLoading ||
      harAvailability.harIsPresent ||
      (harAvailability.harCount ?? 0) > 0);
  const canExpand =
    hasResult ||
    Boolean(hasArgs && (shouldShowArgs || usesMcpToolCallViewer)) ||
    hasHarContent;
  const shouldMountMcpViewer = usesMcpToolCallViewer && Boolean(hasArgs);
  const harRecoveredOutput =
    isHarGatedHttpTool &&
    ((harAvailability.harCount ?? 0) > 0 || harAvailability.harIsPresent);
  const showErrorStatusIcon = hasError && !harRecoveredOutput;
  const shouldHideHarGatedEmptyHttpCard =
    !showWithoutHar &&
    isHarGatedHttpTool &&
    isFinishedToolCall &&
    !hasError &&
    !harAvailability.isLoading &&
    (harAvailability.harCount ?? 0) === 0 &&
    !harAvailability.harIsPresent;

  if (shouldHideHarGatedEmptyHttpCard) {
    return null;
  }

  const StatusIcon = () => {
    if (showErrorStatusIcon)
      return (
        <Box color="fg.error" display="flex" alignItems="center">
          <Cancel01Icon size={10} />
        </Box>
      );

    return null;
  };

  const showShimmer = shouldShowTraceToolShimmer({
    hasResult,
    status: toolCall.status,
  });
  const showBrowserStream =
    onSSESubscribe &&
    isTraceBrowserStreamingTool(normalizedName) &&
    !isFinishedToolCall;
  const browserStreamFrame = showBrowserStream ? (
    <AiBrowserStreamingFrame
      streamingId={toolCall.id}
      onSSESubscribe={onSSESubscribe}
    />
  ) : null;
  const shimmerStyle = showShimmer
    ? {
        background:
          'linear-gradient(90deg, var(--colors-fg-muted) 25%, var(--colors-fg-subtle) 50%, var(--colors-fg-muted) 75%)',
        backgroundSize: '200% 100%',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        animationTimingFunction: 'linear',
      }
    : undefined;
  const toolNameText = (
    <MetaDefault
      color="fg.muted"
      animationStyle={showShimmer ? 'shimmer' : undefined}
      style={shimmerStyle}
    >
      {displayName}
    </MetaDefault>
  );

  if (!canExpand) {
    return (
      <VStack alignItems="stretch" gap={2} width="100%">
        <HStack w="full" gap={1.5} alignItems="center" py={1} borderRadius="sm">
          {toolNameText}
          <Box flex={1} />
          <Box display="flex" alignItems="center">
            <StatusIcon />
          </Box>
        </HStack>
        {browserStreamFrame}
      </VStack>
    );
  }

  return (
    <Collapsible.Root
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
      lazyMount
      unmountOnExit
    >
      <Collapsible.Trigger asChild>
        <HStack
          w="full"
          gap={1.5}
          alignItems="center"
          py={1}
          cursor="pointer"
          borderRadius="sm"
        >
          {toolNameText}

          <TraceExpandChevron
            isExpanded={isOpen}
            tracePhase={tracePhase}
            onHideMessage={onHideMessage}
          />

          <Box flex={1} />

          <Box display="flex" alignItems="center">
            <StatusIcon />
          </Box>
        </HStack>
      </Collapsible.Trigger>

      <Collapsible.Content>
        <VStack alignItems="stretch" gap={2} width="100%" py={2}>
          {browserStreamFrame}

          {endToolBody ? (
            <Box width="100%" maxH="500px" overflow="auto">
              <MarkdownPreview source={endToolBody} fontSize={13} compact />
            </Box>
          ) : (
            shouldShowArgs &&
            hasArgs && (
              <ToolArgsRenderer
                toolName={toolCall.name}
                args={toolCall.args}
                compact={false}
              />
            )
          )}

          {shouldShowErrorBlock && toolCall.error && (
            <Box
              width="100%"
              borderLeftWidth="2px"
              borderLeftColor="border.error"
              pl={2}
              py={2}
            >
              <MetaDefault color="fg.error">{toolCall.error}</MetaDefault>
            </Box>
          )}

          {renderConfig.preferHarOverToolOutput ? (
            <PythonToolHarSection
              toolCallId={toolCall.id}
              toolName={toolCall.name}
              refreshKey={toolCall.status}
              presentation="inline"
              waitForHar
              harAvailability={isHarGatedHttpTool ? harAvailability : undefined}
              fallback={
                toolOutputForRender ? (
                  <Box
                    width="100%"
                    maxH={
                      renderConfig.constrainOutputHeight ? '300px' : undefined
                    }
                    overflow={
                      renderConfig.constrainOutputHeight ? 'auto' : undefined
                    }
                  >
                    {renderToolOutput({
                      toolName: toolCall.name,
                      output: toolOutputForRender,
                      args: toolCall.args,
                      error: toolCall.error,
                      toolCallId: toolCall.id,
                    })}
                  </Box>
                ) : (
                  <MetaDefault color="fg.muted" fontStyle="italic">
                    {t('noOutput')}
                  </MetaDefault>
                )
              }
            />
          ) : (
            <>
              {toolOutputForRender || shouldMountMcpViewer ? (
                <Box
                  width="100%"
                  maxH={
                    renderConfig.constrainOutputHeight ? '300px' : undefined
                  }
                  overflow={
                    renderConfig.constrainOutputHeight ? 'auto' : undefined
                  }
                >
                  {renderToolOutput({
                    toolName: toolCall.name,
                    output: toolOutputForRender ?? '',
                    args: toolCall.args,
                    error: toolCall.error,
                    toolCallId: toolCall.id,
                  })}
                </Box>
              ) : (
                !isEndTestTool && (
                  <MetaDefault color="fg.muted" fontStyle="italic">
                    {t('noOutput')}
                  </MetaDefault>
                )
              )}

              {isFinishedToolCall &&
                !isEndTestTool &&
                !outputShowsHttpExchange &&
                shouldShowToolHarSection(toolCall.name) && (
                  <PythonToolHarSection
                    toolCallId={toolCall.id}
                    toolName={toolCall.name}
                    refreshKey={toolCall.status}
                  />
                )}
            </>
          )}
        </VStack>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}
